import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getProfile, updateProfile, changePassword } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    ArrowLeft, 
    Settings, 
    User,
    Building2,
    Lock,
    Loader2,
    Save,
    Eye,
    EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

export default function UserSettingsPage() {
    const navigate = useNavigate();
    const { user: authUser, isOperator } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Profile data
    const [profile, setProfile] = useState({
        nome: '',
        cognome: '',
        email: '',
        telefono: '',
        ragione_sociale: '',
        partita_iva: '',
        indirizzo: '',
        citta: '',
        cap: '',
        provincia: ''
    });
    
    // Password change
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [showPasswords, setShowPasswords] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        try {
            const response = await getProfile();
            setProfile({
                nome: response.data.nome || '',
                cognome: response.data.cognome || '',
                email: response.data.email || '',
                telefono: response.data.telefono || '',
                ragione_sociale: response.data.ragione_sociale || '',
                partita_iva: response.data.partita_iva || '',
                indirizzo: response.data.indirizzo || '',
                citta: response.data.citta || '',
                cap: response.data.cap || '',
                provincia: response.data.provincia || ''
            });
        } catch (error) {
            toast.error('Errore nel caricamento del profilo');
        } finally {
            setLoading(false);
        }
    };

    const handleProfileChange = (e) => {
        setProfile({ ...profile, [e.target.name]: e.target.value });
    };

    const handleSaveProfile = async () => {
        setSaving(true);
        try {
            await updateProfile(profile);
            toast.success('Profilo aggiornato');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const handleChangePassword = async () => {
        if (passwordData.new_password !== passwordData.confirm_password) {
            toast.error('Le password non coincidono');
            return;
        }
        
        if (passwordData.new_password.length < 6) {
            toast.error('La nuova password deve essere di almeno 6 caratteri');
            return;
        }

        setChangingPassword(true);
        try {
            await changePassword(passwordData.current_password, passwordData.new_password);
            toast.success('Password aggiornata');
            setPasswordData({
                current_password: '',
                new_password: '',
                confirm_password: ''
            });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nel cambio password');
        } finally {
            setChangingPassword(false);
        }
    };

    const goBack = () => {
        if (isOperator()) {
            navigate('/operator');
        } else {
            navigate('/dashboard');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={goBack}
                                className="text-slate-600"
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Indietro
                            </Button>
                            <div className="flex items-center gap-2">
                                <Settings className="w-5 h-5 text-indigo-500" />
                                <h1 className="text-lg font-semibold text-slate-900">Impostazioni</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Tabs defaultValue="profile" className="space-y-6">
                    <TabsList className="bg-white border">
                        <TabsTrigger value="profile" className="gap-2">
                            <User className="w-4 h-4" />
                            Profilo
                        </TabsTrigger>
                        <TabsTrigger value="company" className="gap-2">
                            <Building2 className="w-4 h-4" />
                            Dati Aziendali
                        </TabsTrigger>
                        <TabsTrigger value="security" className="gap-2">
                            <Lock className="w-4 h-4" />
                            Sicurezza
                        </TabsTrigger>
                    </TabsList>

                    {/* Profile Tab */}
                    <TabsContent value="profile">
                        <Card>
                            <CardHeader>
                                <CardTitle>Informazioni Personali</CardTitle>
                                <CardDescription>Gestisci i tuoi dati personali</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="nome">Nome</Label>
                                        <Input
                                            id="nome"
                                            name="nome"
                                            value={profile.nome}
                                            onChange={handleProfileChange}
                                            className="mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="cognome">Cognome</Label>
                                        <Input
                                            id="cognome"
                                            name="cognome"
                                            value={profile.cognome}
                                            onChange={handleProfileChange}
                                            className="mt-1"
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        value={profile.email}
                                        disabled
                                        className="mt-1 bg-slate-50"
                                    />
                                    <p className="text-xs text-slate-500 mt-1">L'email non può essere modificata</p>
                                </div>
                                
                                <div>
                                    <Label htmlFor="telefono">Telefono</Label>
                                    <Input
                                        id="telefono"
                                        name="telefono"
                                        value={profile.telefono}
                                        onChange={handleProfileChange}
                                        className="mt-1"
                                    />
                                </div>

                                <div className="flex justify-end pt-4">
                                    <Button 
                                        onClick={handleSaveProfile}
                                        disabled={saving}
                                        className="bg-indigo-500 hover:bg-indigo-600"
                                    >
                                        {saving ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Save className="w-4 h-4 mr-2" />
                                        )}
                                        Salva Modifiche
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Company Tab */}
                    <TabsContent value="company">
                        <Card>
                            <CardHeader>
                                <CardTitle>Dati Aziendali</CardTitle>
                                <CardDescription>Informazioni sulla tua azienda</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="sm:col-span-2">
                                        <Label htmlFor="ragione_sociale">Ragione Sociale</Label>
                                        <Input
                                            id="ragione_sociale"
                                            name="ragione_sociale"
                                            value={profile.ragione_sociale}
                                            onChange={handleProfileChange}
                                            className="mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="partita_iva">Partita IVA</Label>
                                        <Input
                                            id="partita_iva"
                                            name="partita_iva"
                                            value={profile.partita_iva}
                                            onChange={handleProfileChange}
                                            className="mt-1"
                                        />
                                    </div>
                                </div>

                                <Separator />

                                <div>
                                    <Label htmlFor="indirizzo">Indirizzo</Label>
                                    <Input
                                        id="indirizzo"
                                        name="indirizzo"
                                        value={profile.indirizzo}
                                        onChange={handleProfileChange}
                                        className="mt-1"
                                    />
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    <div className="col-span-2">
                                        <Label htmlFor="citta">Città</Label>
                                        <Input
                                            id="citta"
                                            name="citta"
                                            value={profile.citta}
                                            onChange={handleProfileChange}
                                            className="mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="cap">CAP</Label>
                                        <Input
                                            id="cap"
                                            name="cap"
                                            value={profile.cap}
                                            onChange={handleProfileChange}
                                            className="mt-1"
                                            maxLength={5}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="provincia">Prov.</Label>
                                        <Input
                                            id="provincia"
                                            name="provincia"
                                            value={profile.provincia}
                                            onChange={handleProfileChange}
                                            className="mt-1"
                                            maxLength={2}
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <Button 
                                        onClick={handleSaveProfile}
                                        disabled={saving}
                                        className="bg-indigo-500 hover:bg-indigo-600"
                                    >
                                        {saving ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Save className="w-4 h-4 mr-2" />
                                        )}
                                        Salva Modifiche
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Security Tab */}
                    <TabsContent value="security">
                        <Card>
                            <CardHeader>
                                <CardTitle>Cambio Password</CardTitle>
                                <CardDescription>Aggiorna la tua password di accesso</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label htmlFor="current_password">Password Attuale</Label>
                                    <div className="relative mt-1">
                                        <Input
                                            id="current_password"
                                            name="current_password"
                                            type={showPasswords ? "text" : "password"}
                                            value={passwordData.current_password}
                                            onChange={handlePasswordChange}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-0 top-0 h-full px-3"
                                            onClick={() => setShowPasswords(!showPasswords)}
                                        >
                                            {showPasswords ? (
                                                <EyeOff className="w-4 h-4" />
                                            ) : (
                                                <Eye className="w-4 h-4" />
                                            )}
                                        </Button>
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="new_password">Nuova Password</Label>
                                    <Input
                                        id="new_password"
                                        name="new_password"
                                        type={showPasswords ? "text" : "password"}
                                        value={passwordData.new_password}
                                        onChange={handlePasswordChange}
                                        className="mt-1"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="confirm_password">Conferma Nuova Password</Label>
                                    <Input
                                        id="confirm_password"
                                        name="confirm_password"
                                        type={showPasswords ? "text" : "password"}
                                        value={passwordData.confirm_password}
                                        onChange={handlePasswordChange}
                                        className="mt-1"
                                    />
                                </div>

                                <div className="flex justify-end pt-4">
                                    <Button 
                                        onClick={handleChangePassword}
                                        disabled={changingPassword || !passwordData.current_password || !passwordData.new_password}
                                        className="bg-indigo-500 hover:bg-indigo-600"
                                    >
                                        {changingPassword ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Lock className="w-4 h-4 mr-2" />
                                        )}
                                        Cambia Password
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
